import axios, { type AxiosResponse } from 'axios';

import configByEnvironment from '../config/config.json';

type Environment = keyof typeof configByEnvironment;
type ResizeMode = 'crop' | 'bound' | 'fit';

interface ResizeOptions {
  width?: number;
  height?: number;
  cropType?: ResizeMode;
  additionalParams?: string;
}

interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: XMLHttpRequestBodyInit | null;
}

export interface MediaUploadResponse {
  media_url: string;
  signed_url: string;
}

export interface UploadStatus {
  progress: number;
  status: 'uploading' | 'completed' | 'failed';
}

type UploadCallback = (response: AxiosResponse<MediaUploadResponse>) => void;
type UploadStatusCallback = (status: UploadStatus) => void;

const env = process.env.NODE_ENV || 'development';
const config =
  configByEnvironment[env as Environment] || configByEnvironment.development;

/**
 * @deprecated
 */
export const Resize = {
  // TODO https://docs.cloudimage.io/go/cloudimage-documentation-v7/en/image-resizing
  //  `func=crop` => cover, `func=bound` => contain, can also contain with blurred background with `func=fit&bg_img_fit=1&bg_opacity=0.75`
  //  https://docs.cloudimage.io/go/cloudimage-documentation-v7/en/image-resizing/
  thumbnail: (imageUrl: string, _options: ResizeOptions = {}): string =>
    imageUrl, // `https://aouikjkrpo.cloudimg.io/v7/${imageUrl}?w=200&h=200&func=${cropType}&force_format=webp${additionalParams || ''}`,
  size: (imageUrl: string, _options: ResizeOptions = {}): string => imageUrl, // `https://aouikjkrpo.cloudimg.io/v7/${imageUrl}?w=${width}&h=${height}&func=${cropType}&force_format=webp${additionalParams || ''}`,
  fromCDN: (
    originalUrl: string,
    prefix: string,
    { width = 500, height = 500 }: ResizeOptions = {}
  ): string => originalUrl.replace(prefix, `${width}x${height}${prefix}`),
};

function request(
  url: string,
  opts: RequestOptions = {},
  onProgress?: (event: ProgressEvent<XMLHttpRequestEventTarget>) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method || 'GET', url);
    Object.entries(opts.headers || {}).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });
    xhr.onload = () => resolve(xhr.responseText);
    xhr.onerror = () => reject(new Error('Upload request failed'));
    if (xhr.upload && onProgress) xhr.upload.onprogress = onProgress;
    xhr.send(opts.body ?? null);
  });
}

export const uploadToS3 = (
  file: Blob,
  folder: string,
  filename: string,
  uuid: string,
  callback?: UploadCallback | null,
  setUploadStatus?: UploadStatusCallback
): Promise<AxiosResponse<MediaUploadResponse>> =>
  new Promise<AxiosResponse<MediaUploadResponse>>((resolve, reject) => {
    axios
      .post<MediaUploadResponse>(
        `${config.mediaEndPoint}/sign`,
        {},
        {
          params: {
            uuid,
            filename,
            filetype: file.type,
            type: folder,
          },
        }
      )
      .then((result) => {
        console.debug('Signed response:');
        console.debug(JSON.stringify(result.data.signed_url));
        const options = {
          method: 'PUT',
          body: file,
        };

        if (typeof callback === 'function') {
          callback(result);
        }

        console.debug('Direct upload in progress...');
        // INFO This wasn't working consistently with Axios
        request(result.data.signed_url, options, (event) => {
          console.log('Upload progress:');
          const progress = event.lengthComputable
            ? Math.round((event.loaded / event.total) * 100)
            : 0;
          console.log(progress);
          if (typeof setUploadStatus === 'function') {
            setUploadStatus({ progress, status: 'uploading' });
          }
        })
          .then(() => {
            console.debug('S3 success');
            if (typeof setUploadStatus === 'function') {
              setUploadStatus({ progress: 100, status: 'completed' });
            }
            resolve(result);
          })
          .catch((err) => {
            console.debug('S3 failure:');
            console.debug(err);
            if (typeof setUploadStatus === 'function') {
              setUploadStatus({ progress: 0, status: 'failed' });
            }
            reject(err);
          });
      })
      .catch((err) => {
        console.debug('Failed');
        reject(err);
      });
  });
